import React, { useEffect, useState } from 'react';
import {
    __ConvertBoxConfirmOrderDetailModalProps,
    __ConvertBoxConfirmOrderDetailModalRef,
} from './orderDetailModal.interface';
import { ConfigProvider, Divider, Modal } from 'antd';
import { IoIosClose } from 'react-icons/io';
import CoinIcon from 'utilities/components/coinIcon/coinIcon.index';
import { Enum, GetAllCoinType } from 'interfaces';
import { get, irtToFixed, priceDigit, userProfile } from 'scripts';
import { Alert, Button } from '30pay/components';
import { __TypeOfOrderEnum } from '../initial/initial.interface';
import { __ResultContentProps } from './resultContent/resultContent.interface';
import ConvertBoxResultContent from './resultContent/resultContent.index';
import { API } from 'data/30pay.api';
export interface ConvertBoxConfirmOrderDetailModalRef extends __ConvertBoxConfirmOrderDetailModalRef {}

interface Commission {
    level1?: number;
    level2?: number;
    isVip?: number;
}

const ConvertBoxConfirmOrderModal: React.ForwardRefRenderFunction<
    __ConvertBoxConfirmOrderDetailModalRef,
    __ConvertBoxConfirmOrderDetailModalProps
> = (props: __ConvertBoxConfirmOrderDetailModalProps, ref) => {
    const [coin, setCoin] = useState<GetAllCoinType | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [commission, setCommission] = useState<Commission>();
    const [resultContent, setResultContent] = useState<__ResultContentProps | undefined>();
    const [fetchLoading, setFetchLoading] = useState(false);
    React.useImperativeHandle(ref, () => ({
        startLoading: () => setIsLoading(true),
        stopLoading: () => setIsLoading(false),
        showResultContent: (
            isSuccess: boolean,
            trackingCode?: string,
            extraData?: {
                sourceAmount?: number;
                state: Enum;
                destinationAmount?: number;
            },
        ) => {
            if (isSuccess)
                setResultContent({
                    ...props,
                    onClose: () => props.onClose(true),
                    isOpen: props.isOpen,
                    isSuccess: isSuccess,
                    trackingCode: trackingCode ?? '-',
                    date: new Date().toISOString(),
                    extraData,
                });
        },
    }));

    useEffect(() => {
        setResultContent(undefined);
    }, [props.isOpen]);

    useEffect(() => {
        if (props.data?.typeOfOrder === __TypeOfOrderEnum.buy) setCoin(props.data?.destCoin);
        else setCoin(props.data?.srcCoin);
    }, [props.data]);

    useEffect(() => {
        if (props.isOpen) {
            setFetchLoading(true);
            get<Commission>(API.siteSettings.commissions)
                .then((res) => {
                    const data = res.value.value;
                    setCommission(data);
                })
                .catch((err) => console.log(err))
                .finally(() => setFetchLoading(false));
        }
    }, [props.data, props.isOpen]);

    function extractStateEnumText(state: __TypeOfOrderEnum) {
        switch (state) {
            case __TypeOfOrderEnum.buy:
                return 'خرید';
            case __TypeOfOrderEnum.sell:
                return 'فروش';
            default:
                return 'تبادل';
        }
    }
    function getUserCommissionValue(): number {
        if (!commission) return 0;

        const profile = userProfile.get();
        if (!profile) return 0;

        if (profile.isVip && commission.isVip !== undefined) {
            return commission.isVip;
        }

        const levelUniqueId = Number(profile.level?.uniqueName?.id);

        if (levelUniqueId === 2 && commission.level1 !== undefined) {
            return commission.level1;
        }

        if (levelUniqueId === 3 && commission.level2 !== undefined) {
            return commission.level2;
        }

        if (commission.level1 !== undefined) return commission.level1;
        if (commission.level2 !== undefined) return commission.level2;
        if (commission.isVip !== undefined) return commission.isVip;

        return 0;
    }
    return (
        <ConfigProvider
            theme={{
                components: {
                    Modal: {
                        titleFontSize: 20,
                    },
                },
                token: {
                    borderRadius: 16,
                    padding: 24,
                },
            }}
        >
            <Modal
                loading={fetchLoading}
                styles={{
                    content: {
                        border: '1px solid #D2DEFE',
                    },
                }}
                destroyOnClose
                centered
                footer={null}
                closeIcon={
                    <div className={'w-5 h-5 flex items-center justify-center border border-black-100 rounded-[4px]'}>
                        <IoIosClose color={'black'} />
                    </div>
                }
                open={props.isOpen}
                onClose={() => {
                    props.onClose(!!resultContent);
                }}
                onCancel={() => {
                    props.onClose(!!resultContent);
                }}
                title={resultContent ? 'نتیجه سفارش' : 'پیش فاکتور'}
            >
                <Divider />
                {!resultContent ? (
                    <div className="flex flex-col gap-6">
                        {coin && (
                            <div className="flex items-center gap-3 text-darkPurple-100">
                                <CoinIcon symbol={coin.symbol} />
                                <label className="font-bold text-base">{coin.name}</label>
                                <span className="font-medium text-sm en">{coin.symbol}</span>
                            </div>
                        )}
                        {props.data && (
                            <div className="flex flex-col gap-4 w-full">
                                {props.data.typeOfOrder !== undefined && (
                                    <div className="flex items-center w-full">
                                        <label>نوع سفارش</label>
                                        <span className="flex-1" />
                                        <p>{extractStateEnumText(props.data.typeOfOrder)}</p>
                                    </div>
                                )}
                                {props.data.srcValue !== undefined && (
                                    <div className="flex items-center w-full">
                                        <label>مقدار پرداختی</label>
                                        <span className="flex-1" />
                                        <p>
                                            {irtToFixed(props.data.srcCoin?.symbol ?? '', props.data.srcValue)}{' '}
                                            {props.data.srcCoin?.name}
                                        </p>
                                    </div>
                                )}
                                {props.data.srcValue !== undefined && (
                                    <div className="flex items-center w-full">
                                        <label>مقدار دریافتی</label>
                                        <span className="flex-1" />
                                        <p>
                                            {irtToFixed(props.data.destCoin?.symbol ?? '', props.data.destValue)}{' '}
                                            {props.data.destCoin?.name}
                                        </p>
                                    </div>
                                )}
                                {props.data.srcPrice !== undefined && props.data.destPrice !== undefined && (
                                    <div className="flex items-center w-full">
                                        <label>قیمت واحد</label>
                                        <span className="flex-1" />
                                        <p>
                                            {priceDigit(
                                                props.data.typeOfOrder === __TypeOfOrderEnum.swap
                                                    ? props.data.srcPrice.cryptoIrtSellPrice /
                                                          props.data.destPrice.cryptoIrtBuyPrice
                                                    : props.data.typeOfOrder === __TypeOfOrderEnum.sell
                                                      ? props.data.srcPrice.cryptoIrtBuyPrice
                                                      : props.data.destPrice.cryptoIrtSellPrice,
                                            )}{' '}
                                            {props.data.typeOfOrder === __TypeOfOrderEnum.buy
                                                ? props.data.srcCoin?.name
                                                : props.data.destCoin?.name}
                                        </p>
                                    </div>
                                )}
                                {props.data.network !== undefined && (
                                    <div className="flex items-center w-full">
                                        <label>شبکه</label>
                                        <span className="flex-1" />
                                        <p className="en">
                                            {props.data.network.network?.toUpperCase()} /{' '}
                                            {props.data.network.name?.toUpperCase()}
                                        </p>
                                    </div>
                                )}
                                {props.data.network !== undefined && 'withdrawFee' in props.data.network && (
                                    <div className="flex items-center w-full">
                                        <label>کارمزد شبکه</label>
                                        <span className="flex-1" />
                                        <p style={{ direction: 'rtl' }} className="en">
                                            {priceDigit(props.data.network.withdrawFee ?? '-')} {coin?.name}
                                        </p>
                                    </div>
                                )}
                                {commission !== undefined && (
                                    <div className="flex items-center w-full">
                                        <label>کارمزد تبدیل</label>
                                        <span className="flex-1" />
                                        <p style={{ direction: 'rtl' }} className="en">
                                            {priceDigit(getUserCommissionValue())} تومان
                                        </p>
                                    </div>
                                )}
                                {props.data.walletAddress !== undefined && (
                                    <div className="flex items-center w-full">
                                        <label>آدرس کیف پول</label>
                                        <span className="flex-1" />
                                        <p className="en">{props.data.walletAddress}</p>
                                    </div>
                                )}
                                {props.data.destinationAccount !== undefined && (
                                    <div className="flex items-center w-full">
                                        <label>حساب مقصد</label>
                                        <span className="flex-1" />
                                        <p className="en">{props.data.destinationAccount}</p>
                                    </div>
                                )}
                                {props.data.destValue !== undefined &&
                                    props.data.network !== undefined &&
                                    'withdrawFee' in props.data.network && (
                                        <div className="flex items-center w-full">
                                            <label>مقدار نهایی پس از کسر کارمزد شبکه</label>
                                            <span className="flex-1" />
                                            <p>
                                                {priceDigit(
                                                    props.data.destValue - (props.data.network?.withdrawFee ?? 0),
                                                )}{' '}
                                                {props.data.destCoin?.name}
                                            </p>
                                        </div>
                                    )}
                            </div>
                        )}
                        {coin?.symbol !== 'USDT' && coin?.symbol !== 'UUSD' && (
                            <Alert
                                variant="warning"
                                message={
                                    'مقدار تبدیلی که نمایش داده شده با مقداری که دریافت میکنید یکی نیست و نرخ نهایی در لحظه نهایی شدن تراکنش تعیین میگردد'
                                }
                            />
                        )}
                        <div className="flex items-center w-full gap-4">
                            <Button
                                loading={isLoading}
                                onClick={props.submitButton?.onClick}
                                className="flex-1"
                                type="primary"
                            >
                                {props.submitButton?.title ?? 'ثبت سفارش'}
                            </Button>
                            <Button onClick={() => props.onClose()} className="bg-[#F1F8FF] flex-1" type="text">
                                {resultContent ? 'انصراف' : 'بستن'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <ConvertBoxResultContent {...resultContent} />
                )}
            </Modal>
        </ConfigProvider>
    );
};

export default React.forwardRef(ConvertBoxConfirmOrderModal);
